import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FilterType } from "hooks/search/types";
import { parseComparisonOperator } from "hooks/search/utils";
import { useFilters } from "hooks/search/useFilters";
import { Text } from "components/text";
import { formatSalesDataSummary } from "hooks/search/salesDataFilterUtils";
import { formatAgeValue, isValidFilter } from "./utils";

const useFilterLabel = (filterName: string) => {
  const { t } = useTranslation(["books"]);
  const { filters } = useFilters();
  const filter = useWatch({ name: `filters.${filterName}` });
  const isFilterActive = isValidFilter(filter, new Date());

  if (!isFilterActive) return null;

  switch (filter.type) {
    case FilterType.NUMERIC:
      return `${parseComparisonOperator(filter.comparisonOperator)} ${filter.value}`;
    case FilterType.DATE_RANGE: {
      if (filter.range.from !== filter.range.to) {
        const fromYear = filter.range.from.split(".")[2];
        const toYear = filter.range.to.split(".")[2];
        if (fromYear === toYear) {
          return `${filter.range.from} - ${filter.range.to.split(".").slice(0, 2).join(".")}`;
        }
        return `${filter.range.from} - ${filter.range.to}`;
      }
      return filter.range.from;
    }
    case FilterType.AGE_RANGE:
      return `${formatAgeValue(filter.ageType, filter.range.min, t)} - ${formatAgeValue(filter.ageType, filter.range.max, t)}`;
    case FilterType.CHECKBOX: {
      return filter.values.length > 1
        ? `${filter.values.length} ${filters.find((f) => f.name === filterName)?.badgeLabel}`
        : filter.values[0];
    }
    case FilterType.SALES_DATA:
      return formatSalesDataSummary(
        filter,
        {
          totalUnits: t("books:filters.salesData.totalUnits"),
          amazonOrderedUnits: t("books:filters.salesData.amazonOrderedUnits"),
          amazonConversionRate: t(
            "books:filters.salesData.amazonConversionRate",
          ),
        },
        (count) => t("books:filters.salesData.summary.metrics", { count }),
      );
    default:
      return filter.value;
  }
};

export const ActiveFilterValue = ({ filterName }: { filterName: string }) => {
  const label = useFilterLabel(filterName);
  if (!label) return null;

  return (
    <Text
      as="span"
      variant="caption"
      className="truncate max-w-[120px]"
      aria-label={String(label)}
    >
      {label}
    </Text>
  );
};
